// English source strings of the assistant panel (spec docs/superpowers/specs/2026-09-28-assistant-panel-design.md
// §3.8). Merged into `en`; pt-BR lives in pt-BR-assistant.ts (complete, checked by the type). `assistant.line.<code>`
// renders every rules line code the backend emits (packages/domain/src/assistant.ts, packages/db/src/repositories/
// assistant.ts, packages/engine-temporal/src/agent/assistant-turn.ts, apps/api/src/routes/assistant-turns.ts).
export const enAssistant = {
  "assistant.work.task": "Task",
  "assistant.work.planner": "Planning agent",
  "assistant.work.prd": "PRD version",
  "assistant.work.requested": "Queued",
  "assistant.work.running": "Running",
  "assistant.work.completed": "Planning completed",
  "assistant.work.failed": "Failed",
  "assistant.work.unavailable": "Status unavailable",
  "assistant.work.observed": "Recorded status checked",
  "assistant.timing.started": "Started:",
  "assistant.artifact.partial": "Incomplete — continuation required",
  "assistant.artifact.modelReview": "Reviewed by the generating model; independent review unconfirmed",
  "assistant.artifact.structuralReview": "File bytes checked; content review pending",
  "assistant.artifact.failed": "Could not load the file. You can retry or download it.",
  "assistant.artifact.view": "Read in conversation",
  "assistant.artifacts": "Conversation files",
  "assistant.actionTitle.create_project_request": "Register PRD and plan {title} in the background",
  "assistant.detail.project_request_queued": "PRD saved and planning queued; execution follows the project's policies. You can continue this conversation.",
  "assistant.actionTitle.review_learnings": "Review learnings",
  "assistant.actionTitle.server_operation": "Request server operation on {server}",
  "assistant.actionTitle.github_operation": "GitHub: {operation}",
  "assistant.action.githubRequest": "Review the exact GitHub request",
  "assistant.detail.github_request_completed": "confirmed by the registered GitHub API",
  "assistant.detail.github_request_refused": "GitHub refused this request; inspect permissions and validation",
  "assistant.detail.github_outcome_requires_reconciliation": "outcome unknown; inspect the recorded operation before trying again",
  "assistant.action.requested": "Requested: {label}",
  "assistant.detail.learning_review_queued": "review requested; the learning governor has not finished yet",
  "assistant.detail.server_operation_queued": "queued for the worker; execution has not been confirmed",
  "assistant.action.learningReviewActor": "AI review by service learning-governor; requesting a review does not approve a learning.",
  "assistant.action.serverTarget": "Server: {server}",
  "assistant.action.serverProject": "Project: {project}",
  "assistant.action.serverDisruption": "Declared disruption: {disruption}; the API may raise this level.",
  "assistant.action.serverQueuedHint": "This requests a queued operation. The worker checks host authorization before execution.",
  "assistant.disruption.none": "None",
  "assistant.disruption.application": "Application",
  "assistant.disruption.host": "Host",
  "assistant.title": "Assistant",
  "assistant.timing.elapsed": "Elapsed: {duration}",
  "assistant.timing.duration": "Turn duration: {duration}",
  "assistant.close": "Close assistant",
  "assistant.dock": "Dock panel",
  "assistant.scope.home": "All your projects",
  "assistant.scope.project": "This project",
  "assistant.log": "Conversation with the assistant",
  "assistant.empty": "Start a conversation, ask about your projects, or request a file.",
  "assistant.historyFailed": "The conversation could not be loaded.",
  "assistant.composer.label": "Ask the assistant",
  "assistant.composer.placeholder": "Message the assistant…",
  "assistant.composer.hint": "Enter sends; Shift+Enter adds a line.",
  "assistant.send": "Send",
  "assistant.stop": "Stop",
  "assistant.stopping": "Stopping…",
  "assistant.counter": "{n} / {max}",
  "assistant.tooLong": "Too long: shorten your question to {max} characters.",
  "assistant.thinking": "Working on it…",
  "assistant.progress.model": "Generating a response…",
  "assistant.progress.tools": "Running tools…",
  "assistant.progress.answer": "Writing the reply…",
  "assistant.stillWorking": "Still working on it — try again in a moment.",
  "assistant.checkAgain": "Check again",
  "assistant.turnActive": "A question is already being answered in this conversation. Wait for the reply or press Stop.",
  "assistant.sendFailed": "The question was not sent",
  "assistant.suggestions": "Suggested replies",
  "assistant.nothingToReport": "Nothing needs your attention right now.",
  "assistant.summaryChip": "Project summary",
  "assistant.prompt.summary": "What is waiting for me in this project?",
  "assistant.prompt.readiness": "Is this project ready to start?",

  "assistant.action.notNow": "Not now",
  "assistant.action.dismissed": "Skipped: {label}",
  "assistant.action.running": "Running: {label}…",
  "assistant.action.pending": "Started: {label}",
  "assistant.action.succeeded": "Done: {label}",
  "assistant.action.failed": "Failed: {label}",
  "assistant.action.unknown": "Not confirmed: {label}. We could not confirm whether this action finished. It will not be repeated by itself; check the task or project before trying again.",
  "assistant.action.elsewhere": "Already handled: {label}",
  "assistant.detail.ready": "ready",
  "assistant.detail.not_ready": "not ready",
  "assistant.detail.still_running": "still running",
  // Failure detail tokens (R29: API error codes, never the server's English message).
  "assistant.detail.check_failed": "the check could not run",
  "assistant.detail.version_conflict": "it changed in the meantime",
  "assistant.detail.forbidden": "you are not allowed to do this",
  "assistant.detail.not_found": "it no longer exists",
  "assistant.detail.gate_pending": "an impediment is still open",
  "assistant.detail.policy_denied": "a policy refused it",
  "assistant.detail.validation_failed": "the request was refused as invalid",
  "assistant.detail.rate_limited": "too many requests; try again shortly",
  "assistant.detail.unavailable": "the service is unavailable",
  "assistant.detail.network": "the connection failed",
  "assistant.detail.project_created": "opening its board",
  "assistant.detail.github_unavailable": "no active GitHub connection; connect GitHub in Integrations first",
  "assistant.detail.repository_exists": "a repository with that name already exists",

  // Action cards (R29): fixed title/button per type + args; {task} is "#N", {project} a project name.
  "assistant.actionTitle.validate_project": "Validate the project",
  "assistant.actionTitle.open_page": "Open {page}",
  "assistant.actionTitle.open_page_of": "Open {page} — {project}",
  "assistant.actionTitle.open_page_other": "Open {page} of another project",
  "assistant.actionTitle.retry_task": "Retry task {task}",
  "assistant.actionTitle.unblock_task": "Unblock task {task}",
  "assistant.actionTitle.resource_available": "Mark the resource available for task {task}",
  "assistant.actionTitle.start_project": "Start the project",
  "assistant.actionTitle.resume_project": "Resume the project",
  "assistant.actionTitle.create_task": "Create the task “{title}”",
  "assistant.actionTitle.add_guidance": "Add guidance to task {task}",
  "assistant.actionTitle.open_settings_form": "Fix {field} in the project settings",
  "assistant.actionTitle.decide_approval": "Answer the request — option {option}",
  "assistant.actionTitle.connect_repository": "Connect repository",
  "assistant.actionTitle.create_project_from_brief": "Create the project “{name}”",
  "assistant.field.repository_url": "the repository URL",
  "assistant.field.base_branch": "the base branch",
  "assistant.action.guidance": "Guidance: {text}",
  "assistant.action.reviewBrief": "Review the brief ({count} characters)",
  // D34 (assistant as operator)
  "assistant.actionTitle.create_task_start": "Create and start the task “{title}”",
  "assistant.actionTitle.update_project_settings": "Update the project settings: {fields}",
  "assistant.actionTitle.set_execution_policy_on": "Turn on sandboxed command execution",
  "assistant.actionTitle.set_execution_policy_off": "Turn off command execution",
  "assistant.actionTitle.edit_task": "Edit task {task}",
  "assistant.actionTitle.move_task": "Task {task}: {move}",
  "assistant.actionTitle.cancel_task": "Cancel task {task}",
  "assistant.actionTitle.start_task_run": "Start the agents on task {task}",
  "assistant.actionTitle.pause_project": "Pause the project",
  "assistant.actionTitle.drain_project": "Drain the project (finish running tasks, start no new ones)",
  "assistant.actionTitle.run_model_probe": "Probe the model's capabilities (up to {cents})",
  "assistant.actionTitle.update_agent_profile": "Change an agent profile",
  "assistant.actionTitle.update_model_target_disabled": "Disable a model target",
  "assistant.actionTitle.update_model_target_unverified": "Send a model target back to verification",
  "assistant.actionTitle.update_provider_connection_disabled": "Disable a provider connection",
  "assistant.actionTitle.update_provider_connection_draft": "Return a provider connection to draft",
  "assistant.actionTitle.activate_integration": "Activate an integration",
  "assistant.actionTitle.activate_routing_config": "Activate a routing configuration",
  "assistant.actionTitle.publish_project": "Publish the app",
  "assistant.field.name": "name",
  "assistant.field.description": "description",
  "assistant.field.test_command": "test command",
  "assistant.field.build_command": "build command",
  "assistant.field.workspace_subpath": "workspace subpath",
  "assistant.move.mark_ready": "move to To Do",
  "assistant.move.pause": "pause",
  "assistant.move.resume": "resume",
  "assistant.move.reopen": "reopen",
  "assistant.move.block": "block",
  "assistant.action.refused": "Not allowed for your role: {label}",
  "assistant.followUp.prompt": "Continue with the results of these actions.",
  "assistant.followUp.note": "Automatic follow-up {n} of {max}",
  "assistant.detail.no_workspace": "the project has no workspace yet",
  "assistant.detail.policy_on": "on while the worker sandbox is verified",
  "assistant.detail.policy_off": "commands are refused now",
  "assistant.detail.task_started": "execution accepted; follow the recorded task status for actual progress",
  "assistant.detail.task_queued": "created and prioritized; it starts when the project can run it",
  "assistant.detail.already_running": "it was already running",
  "assistant.detail.publish_started": "deployment requested; waiting for completion",
  "assistant.detail.project_not_active": "the project is not running",
  "assistant.detail.at_capacity": "every task slot is busy",
  "assistant.detail.task_not_startable": "the task is not in a column that can start",
  "assistant.detail.invalid_transition": "that move is not allowed from the current state",
  "assistant.detail.execution_policy_not_settable": "the execution policy is set by an administrator",
  // D36 (system operator on Home)
  "assistant.detail.planning_started": "planning started",
  "assistant.detail.guardian_queued": "the guardian will run shortly",
  "assistant.detail.guardian_action_requested": "requested; the worker carries it out",
  "assistant.detail.no_system_project": "no MaestrOS project was found",
  "assistant.actionTitle.start_planning_run": "Start planning from the PRD",
  "assistant.actionTitle.create_system_task": "Create the MaestrOS task “{title}”",
  "assistant.actionTitle.create_system_task_start": "Create and start the MaestrOS task “{title}”",
  "assistant.actionTitle.run_guardian": "Run the Server Guardian now",
  "assistant.actionTitle.guardian_incident_action_restart": "Restart the application of the incident",
  "assistant.actionTitle.guardian_incident_action_redeploy": "Redeploy the application of the incident",
  // D37.3 (content language)
  "assistant.actionTitle.translate_tasks": "Translate the tasks not started yet into the content language",
  "assistant.detail.translation_queued": "translation queued; a paid model asks for confirmation on the Kanban",
  "assistant.detail.nothing_to_translate": "there is no task left to translate",
  // Rules-path reason codes (packages/domain/src/assistant.ts rawAction).
  "assistant.actionReason.readiness.never_checked": "This project was never checked for readiness.",
  "assistant.actionReason.readiness.stale": "The last readiness result is stale; check again before relying on it.",
  "assistant.actionReason.readiness.setting": "This project setting needs attention.",
  "assistant.actionReason.readiness.no_route": "No model route is eligible for this project yet.",
  "assistant.actionReason.readiness.connect_repository": "Connect the project's GitHub repository to clone it and detect its test command.",
  "assistant.actionReason.summary.waiting_for_you": "There are items waiting for you.",
  "assistant.actionReason.task.infra_failure": "This task failed on an infrastructure error and was never retried.",
  "assistant.actionReason.task.blocked": "This task is blocked.",
  "assistant.actionReason.home.needs_attention": "This project needs attention.",

  "assistant.mode.rules": "Rules mode: answered from MaestrOS data without AI.",
  "assistant.mode.not_configured": "Rules mode: the AI is not configured yet.",
  "assistant.mode.budget_exhausted": "Rules mode: today's AI budget for the assistant is used up.",
  "assistant.diagnostics.summary": "Diagnostics",
  "assistant.diagnostics.profile": "Profile: {id}",
  "assistant.diagnostics.version": "Version: {id}",
  "assistant.diagnostics.noVersion": "unversioned",
  "assistant.diagnostics.capabilities": "Capabilities ({count}): {list}",
  "assistant.budgetHigh": "80% of today's assistant budget used.",

  // Rules lines (one per backend code)
  "assistant.line.summary.waiting_for_you": "Items waiting for you: {count}.",
  "assistant.line.summary.blocked": "Blocked tasks: {count}.",
  "assistant.line.summary.waiting_resource": "Tasks waiting on a resource: {count}.",
  "assistant.line.summary.failed": "Failed tasks: {count}.",
  "assistant.line.summary.progress": "{done} of {total} tasks done.",
  "assistant.line.home.waiting_for_you": "Items waiting for you across your projects: {count}.",
  "assistant.line.home.blocked": "Blocked tasks across your projects: {count}.",
  "assistant.line.readiness.failed.repository": "The repository check failed: MaestrOS could not reach the project's repository.",
  "assistant.line.readiness.failed.base_branch": "The base branch check failed: the configured base branch was not found in the repository.",
  "assistant.line.readiness.failed.workspace_authorized": "The workspace is not authorized for this project yet.",
  "assistant.line.readiness.failed.runner_healthy": "No healthy runner is available to execute tasks.",
  "assistant.line.readiness.failed.executor_available": "No executor is available to run the tasks.",
  "assistant.line.readiness.failed.test_command": "The test command check failed: set a test command the runner can execute.",
  "assistant.line.readiness.failed.eligible_route": "No model route is eligible for this project yet.",
  "assistant.line.readiness.unknown.repository": "The repository check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.base_branch": "The base branch check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.workspace_authorized": "The workspace authorization check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.runner_healthy": "The healthy runner check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.executor_available": "The executor check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.test_command": "The test command check could not be decided; validate the project again.",
  "assistant.line.readiness.unknown.eligible_route": "The model route check could not be decided; validate the project again.",
  "assistant.line.readiness.config_impediments": "The project's effective configuration (agents, routes, Maestro) has {count} impediment(s): see Settings › Routing & Maestro.",
  "assistant.line.readiness.engine_unavailable": "The execution engine is not responding, so no task can run right now.",
  "assistant.line.assistant.engine_unavailable": "The AI engine is unavailable right now, so this answer comes from rules.",
  "assistant.line.access.lost": "You no longer have access to this project, so the assistant cannot answer here.",
  "assistant.line.turn.stopped": "You stopped this answer.",
  "assistant.line.turn.ceiling_reached": "The answer stopped at the per-question limit.",
  "assistant.line.ai.runtime_disabled": "The AI runtime is turned off on this server, so this answer comes from rules.",
  "assistant.line.ai.no_route": "No AI model route is set up for the assistant yet, so this answer comes from rules.",
  "assistant.line.ai.not_allowed": "Real AI calls are not allowed on this server yet, so this answer comes from rules.",
  "assistant.line.ai.no_pricing": "The assistant's model has no price set, so it is not used and this answer comes from rules.",
  "assistant.line.ai.try_again": "The AI could not answer this time. Try again in a moment.",
  "assistant.line.budget.daily_ceiling": "Today's assistant budget is used up, so this answer comes from rules.",
  "assistant.line.budget.unknown_cost": "An earlier assistant call today has an unknown cost, so AI answers are paused until tomorrow.",
  "assistant.line.budget.call_refused": "A spending limit (such as the project's hard limit) blocked the AI call, so this answer comes from rules.",

  // Usage monitoring (Observability page, §3.9, owner request)
  "assistant.usage.title": "Assistant usage",
  "assistant.usage.hint": "Organization-wide spend, questions and actions of the paid assistant panel.",
  "assistant.usage.unknown": "unknown",
  "assistant.usage.today": "Today",
  "assistant.usage.avgPerQuestion": "Avg. per question",
  "assistant.usage.maxPerQuestion": "Max. per question",
  "assistant.usage.stoppedAtCeiling": "Stopped at the per-question ceiling",
  "assistant.usage.unknownCostCalls": "{n} call(s) with unknown cost",
  "assistant.usage.byDay": "Daily spend",
  "assistant.usage.noDays": "No assistant turns in this window.",
  "assistant.usage.modes": "Answers by mode",
  "assistant.usage.mode.ai": "AI",
  "assistant.usage.mode.rules": "Rules",
  "assistant.usage.mode.not_configured": "Not configured",
  "assistant.usage.mode.budget_exhausted": "Budget exhausted",
  "assistant.usage.mode.error": "Error",
  "assistant.usage.byProject": "Spend by project",
  "assistant.usage.noProjects": "No project turns in this window.",
  "assistant.usage.byUser": "Spend by user",
  "assistant.usage.noUsers": "No turns in this window.",
  "assistant.usage.actions": "Actions",
  "assistant.usage.actions.proposed": "Proposed",
  "assistant.usage.actions.autoExecuted": "Auto-executed",
  "assistant.usage.actions.clicked": "Clicked",
  "assistant.usage.actions.failed": "Failed",
  "assistant.usage.col.day": "Day",
  "assistant.usage.col.spent": "Spent",
  "assistant.usage.col.limit": "Ceiling",
  "assistant.usage.col.questions": "Questions",
  "assistant.usage.col.project": "Project",
  "assistant.usage.col.user": "User",
  "assistant.usage.questionsShort": "{n} question(s)",
  "assistant.usage.homeLabel": "Home",
  "assistant.usage.partial": "partial",
  "assistant.usage.noteHomePartial": "Spend totals (today, per day and per project) include every member's questions, Home ones included. Per-person spend, question counts and per-question figures include Home questions only from conversations you can read (your own).",
  "assistant.usage.hintProjectCeiling": "This ceiling is shared by the whole organization, not only this project.",
  "assistant.actionTitle.delete_project": "Delete the project {project}",
  "assistant.detail.project_deleted": "project deleted",
  "projectDelete.dangerZone": "Danger zone",
  "projectDelete.body": "Deleting removes the project, its tasks, runs and settings from MaestrOS. It cannot be undone here. Pause or drain the project first if work is running.",
  "projectDelete.repositoryKept": "The linked GitHub repository is not deleted.",
  "projectDelete.button": "Delete project",
  "projectDelete.typeName": "Type the project name ({name}) to confirm",
  "projectDelete.confirm": "Delete this project",
  "projectDelete.summary": "{name}: {total} tasks ({done} done, {cancelled} cancelled, {inProgress} in progress).",
  "projectDelete.runningWork": "Work is running in this project: pause or drain it first, then delete it.",
  "projectDelete.factsUnavailable": "The project's task counts could not be loaded.",
} as const;
